import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Loader2, Wand2 } from "lucide-react";

import { useAI } from "@/hooks/useAI";
import { useActiveDataset } from "@/contexts/ActiveDatasetContext";
import type { Row } from "@/lib/cleaning-engine";

/**
 * Enrichment adds columns that are DERIVED from the data the user already has.
 * Gemini only chooses which derivation to apply to which column; the values
 * themselves are computed here. Nothing is guessed or invented — no fake
 * demographics, no made-up company data.
 */
type Op =
  | { op: "ratio"; name: string; a: string; b: string }
  | { op: "sum"; name: string; a: string; b: string }
  | { op: "difference"; name: string; a: string; b: string }
  | { op: "percent_of_total"; name: string; a: string }
  | { op: "bucket"; name: string; a: string }
  | { op: "date_part"; name: string; a: string; part: "month" | "weekday" | "year" }
  | { op: "email_domain"; name: string; a: string }
  | { op: "text_length"; name: string; a: string };

const OPS_SPEC = `Choose enrichment operations that can be computed from the columns provided.
Allowed operations (use these exact shapes, nothing else):
{"op":"ratio","name":"<new column>","a":"<numeric column>","b":"<numeric column>"}
{"op":"sum","name":"...","a":"...","b":"..."}
{"op":"difference","name":"...","a":"...","b":"..."}
{"op":"percent_of_total","name":"...","a":"<numeric column>"}
{"op":"bucket","name":"...","a":"<numeric column>"}          // Low / Medium / High by quartile
{"op":"date_part","name":"...","a":"<date column>","part":"month|weekday|year"}
{"op":"email_domain","name":"...","a":"<email column>"}
{"op":"text_length","name":"...","a":"<text column>"}
Return ONLY: {"operations":[...],"reasoning":"..."} with at most 6 operations.
Never invent a column name that is not in the list.`;

const num = (v: any) => {
  const n = Number(String(v ?? "").replace(/[₦$£€,\s]/g, ""));
  return isNaN(n) ? null : n;
};

function applyOps(rows: Row[], ops: Op[]) {
  const applied: { name: string; description: string }[] = [];
  let out = rows.map((r) => ({ ...r }));

  for (const op of ops) {
    if (!op?.name || !("a" in op) || !op.a) continue;
    const cols = Object.keys(out[0] ?? {});
    if (!cols.includes(op.a)) continue;
    if ("b" in op && op.b && !cols.includes(op.b)) continue;

    switch (op.op) {
      case "ratio":
      case "sum":
      case "difference": {
        out = out.map((r) => {
          const a = num(r[op.a]);
          const b = num((r as any)[(op as any).b]);
          let value: number | null = null;
          if (a !== null && b !== null) {
            if (op.op === "ratio") value = b === 0 ? null : Number((a / b).toFixed(4));
            if (op.op === "sum") value = a + b;
            if (op.op === "difference") value = a - b;
          }
          return { ...r, [op.name]: value };
        });
        applied.push({
          name: op.name,
          description: `${op.op} of ${op.a} and ${(op as any).b}`,
        });
        break;
      }
      case "percent_of_total": {
        const total = out.reduce((acc, r) => acc + (num(r[op.a]) ?? 0), 0);
        out = out.map((r) => {
          const a = num(r[op.a]);
          return {
            ...r,
            [op.name]: a === null || total === 0 ? null : Number(((a / total) * 100).toFixed(2)),
          };
        });
        applied.push({ name: op.name, description: `${op.a} as % of total` });
        break;
      }
      case "bucket": {
        const nums = out.map((r) => num(r[op.a])).filter((n): n is number => n !== null).sort((x, y) => x - y);
        if (!nums.length) break;
        const q1 = nums[Math.floor(nums.length * 0.25)];
        const q3 = nums[Math.floor(nums.length * 0.75)];
        out = out.map((r) => {
          const a = num(r[op.a]);
          return {
            ...r,
            [op.name]: a === null ? null : a <= q1 ? "Low" : a >= q3 ? "High" : "Medium",
          };
        });
        applied.push({ name: op.name, description: `${op.a} grouped into Low/Medium/High` });
        break;
      }
      case "date_part": {
        out = out.map((r) => {
          const d = new Date(r[op.a]);
          if (isNaN(d.getTime())) return { ...r, [op.name]: null };
          const value =
            op.part === "year"
              ? d.getFullYear()
              : op.part === "month"
              ? d.toLocaleString("en", { month: "long" })
              : d.toLocaleString("en", { weekday: "long" });
          return { ...r, [op.name]: value };
        });
        applied.push({ name: op.name, description: `${op.part} from ${op.a}` });
        break;
      }
      case "email_domain": {
        out = out.map((r) => {
          const s = String(r[op.a] ?? "");
          const at = s.indexOf("@");
          return { ...r, [op.name]: at > -1 ? s.slice(at + 1).toLowerCase() : null };
        });
        applied.push({ name: op.name, description: `domain from ${op.a}` });
        break;
      }
      case "text_length": {
        out = out.map((r) => ({ ...r, [op.name]: String(r[op.a] ?? "").length }));
        applied.push({ name: op.name, description: `character count of ${op.a}` });
        break;
      }
    }
  }

  return { rows: out, applied };
}

export const DataEnrichment = () => {
  const { dataset, updateRows } = useActiveDataset();
  const { call } = useAI();
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState<{ name: string; description: string }[]>([]);
  const [reasoning, setReasoning] = useState("");

  const runEnrichment = async () => {
    if (!dataset?.rows.length) {
      toast.error("Load a dataset in the cleaner above first.");
      return;
    }
    setLoading(true);
    setAdded([]);
    setReasoning("");
    try {
      const result = await call<any>({
        feature: "data_enrichment",
        input: OPS_SPEC,
        dataset: {
          columns: dataset.columns,
          sample_rows: dataset.rows.slice(0, 25),
          row_count: dataset.rows.length,
        },
      });
      if (!result.success) return;

      let payload: any = result.json;
      if (!payload && result.response) {
        try {
          payload = JSON.parse(result.response);
        } catch {
          payload = null;
        }
      }
      const ops: Op[] = payload?.operations ?? [];
      if (!ops.length) {
        toast.info("The AI didn't find any useful columns to add to this dataset.");
        return;
      }

      const { rows, applied } = applyOps(dataset.rows, ops);
      if (!applied.length) {
        toast.info("None of the suggested columns could be calculated from your data.");
        return;
      }
      updateRows(
        rows,
        applied.map((a) => ({ action: `Added column "${a.name}"`, rows_affected: rows.length })),
      );
      setAdded(applied);
      setReasoning(payload?.reasoning || "");
      toast.success(`Added ${applied.length} new column${applied.length === 1 ? "" : "s"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Add useful columns</h3>
          <p className="text-sm text-muted-foreground">
            The AI suggests extra columns it can calculate from your own data — no
            guessed or external information.
          </p>
        </div>
        <Sparkles className="h-5 w-5 text-primary" />
      </div>

      {dataset ? (
        <p className="text-sm text-muted-foreground">
          Working on <strong>{dataset.label}</strong> ({dataset.columns.length} columns)
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Load a file in the cleaner above to enable this.
        </p>
      )}

      <Button onClick={runEnrichment} disabled={loading || !dataset?.rows.length}>
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Wand2 className="mr-2 h-4 w-4" />
        )}
        Suggest and add columns
      </Button>

      {added.length > 0 && (
        <div className="rounded-lg border p-4 space-y-2">
          <h4 className="text-sm font-medium">Columns added</h4>
          <div className="flex flex-wrap gap-2">
            {added.map((a) => (
              <Badge key={a.name} variant="secondary" title={a.description}>
                {a.name}
              </Badge>
            ))}
          </div>
          <ul className="text-xs text-muted-foreground list-disc ml-5">
            {added.map((a) => (
              <li key={a.name}>
                <strong>{a.name}</strong>: {a.description}
              </li>
            ))}
          </ul>
          {reasoning && <p className="text-sm text-muted-foreground">{reasoning}</p>}
          <p className="text-xs text-muted-foreground">
            Use "Save changes" above to keep these columns.
          </p>
        </div>
      )}
    </Card>
  );
};
